import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Clapperboard, ImagePlus, FolderClock, Download, Loader2, GraduationCap, Lock, Crown, Star, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";

/**
 * Renders a single tool card. Card visual state precedence:
 *   1) `comingSoon` → muted + "COMING SOON" badge, not clickable
 *   2) `locked`     → muted + "Creator & Pro" badge + lock icon, click goes to billing
 *   3) default      → fully clickable, leads to the tool
 */
function ToolCard({ to, icon: Icon, title, desc, openLabel, locked, comingSoon, isNewFeature, lockedBadge, unlockLabel, testid }) {
  if (comingSoon) {
    return (
      <div
        className="relative bg-zinc-50 p-8 border border-dashed border-zinc-300 cursor-not-allowed select-none"
        data-testid={`${testid}-coming-soon`}
        aria-disabled="true"
      >
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-900 text-white text-[10px] font-black uppercase tracking-wider">
          Coming Soon
        </div>
        <Icon className="w-10 h-10 text-zinc-400" strokeWidth={1.5} />
        <h3 className="mt-6 text-2xl font-bold tracking-tight text-zinc-500">{title}</h3>
        <p className="mt-2 text-zinc-400">{desc}</p>
        <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-400">
          {openLabel} <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    );
  }
  if (locked) {
    return (
      <div
        className="relative bg-zinc-50 p-8 border border-dashed border-zinc-300 cursor-not-allowed select-none"
        data-testid={`${testid}-locked`}
        aria-disabled="true"
      >
        <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-zinc-400 via-slate-300 to-zinc-500 text-zinc-900 text-[10px] font-black uppercase tracking-wider shadow-sm">
            <Star className="w-3 h-3" fill="currentColor" /> Creator
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-zinc-900 text-[10px] font-black uppercase tracking-wider shadow-sm">
            <Crown className="w-3 h-3" /> Pro
          </span>
        </div>
        <div className="relative inline-flex">
          <Icon className="w-10 h-10 text-zinc-400" strokeWidth={1.5} />
          <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-zinc-900 text-white">
            <Lock className="w-3 h-3" />
          </span>
        </div>
        <h3 className="mt-6 text-2xl font-bold tracking-tight text-zinc-500">{title}</h3>
        <p className="mt-2 text-zinc-400">{desc}</p>
        <Link
          to="/app/billing"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-amber-800"
          data-testid={`${testid}-upgrade`}
          onClick={(e) => e.stopPropagation()}
        >
          <Crown className="w-4 h-4" /> {unlockLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  return (
    <Link to={to} data-testid={testid} className="bg-white p-8 hover-lift group relative">
      {isNewFeature && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wider shadow-sm">
          <Zap className="w-3 h-3" /> New
        </div>
      )}
      <Icon className="w-10 h-10 text-blue-600" strokeWidth={1.5} />
      <h3 className="mt-6 text-2xl font-bold tracking-tight">{title}</h3>
      <p className="mt-2 text-zinc-600">{desc}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-950 group-hover:text-blue-600">
        {openLabel} <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { isHidden, isComingSoon, isNew } = useFeatureFlags();
  const { t } = useT();
  const [recent, setRecent] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  // Free plan can use chat (up to 5 msg/day) but is locked out of the heavy
  // tools — Video Producer and Interview Practice. Admins always pass.
  const planId = user?.plan || "free";
  const isPaid = !!user?.is_admin || planId === "creator" || planId === "pro";
  const videoLocked = !isPaid;
  const interviewLocked = !isPaid;

  // Feature-flag aware visibility. Admins always see the card (with a Coming
  // Soon banner if applicable) so they can keep navigating.
  const showVideo = user?.is_admin || !isHidden("video_producer");
  const showInterview = user?.is_admin || !isHidden("interview_practice");
  const showChat = user?.is_admin || !isHidden("chat");
  const showImage = user?.is_admin || !isHidden("image_producer");
  const videoComingSoon = isComingSoon("video_producer");
  const imageComingSoon = isComingSoon("image_producer");
  const interviewComingSoon = isComingSoon("interview_practice");
  const chatComingSoon = isComingSoon("chat");
  const videoIsNew = isNew("video_producer");
  const imageIsNew = isNew("image_producer");
  const interviewIsNew = isNew("interview_practice");
  const chatIsNew = isNew("chat");
  const imageLocked = !isPaid;

  useEffect(() => {
    api.get("/projects").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const quickDownload = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadingId(id);
    try {
      const { data } = await api.get(`/projects/${id}`);
      if (!data?.video_url) {
        toast.error("No MP4 available yet");
        return;
      }
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_BACKEND_URL}${data.video_url}`;
      link.download = `lumioos-${(data.title || id).replace(/[^\w-]+/g, "_").slice(0, 60)}.mp4`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("dash_eyebrow")}</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">
            {t("dash_hello")}, {user?.name?.split(" ")[0] || "creator"}.
          </h1>
          <p className="mt-2 text-zinc-600">{t("dash_sub")}</p>
        </div>
      </div>

      {/* Count visible cards so the grid never leaves an empty column behind
          when a feature is set to "hidden". 1 → centered, 2 → 2-col, 3 → 3-col. */}
      {(() => {
        const visibleCount = [showChat, showInterview].filter(Boolean).length;
        const gridCols = visibleCount === 3 ? "md:grid-cols-3" : visibleCount === 2 ? "md:grid-cols-2" : "md:grid-cols-1";
        return (
          <div className={`mt-10 grid grid-cols-1 ${gridCols} gap-px bg-zinc-200 border border-zinc-200`}>
            {showChat && (
              <ToolCard
                testid="tool-chat"
                to="/app/chat"
                icon={MessageSquare}
                title={t("side_chat")}
                desc={t("dash_chat_card_desc")}
                openLabel={t("dash_open")}
                locked={false}
                comingSoon={chatComingSoon}
                isNewFeature={chatIsNew}
              />
            )}
            {showInterview && (
              <ToolCard
                testid="tool-interview"
                to="/app/interview"
                icon={GraduationCap}
                title={t("side_interview")}
                desc={t("dash_interview_card_desc")}
                openLabel={t("dash_open")}
                locked={interviewLocked}
                comingSoon={interviewComingSoon}
                isNewFeature={interviewIsNew}
                lockedBadge={t("dash_pro_only_badge")}
                unlockLabel={t("dash_card_unlock")}
              />
            )}
          </div>
        );
      })()}

      <div className="mt-12">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("dash_recent_eye")}</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">{t("dash_recent_h2")}</h2>
          </div>
          <Link to="/app/projects" className="text-sm font-semibold text-blue-600 hover:underline">{t("dash_view_all")}</Link>
        </div>

        <div className="mt-6 border border-zinc-200 bg-white">
          {recent.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">
              {t("dash_no_projects")}{" "}
              <Link to="/app/chat" className="text-blue-600 font-semibold">{t("side_chat")}</Link>{!videoLocked && (<>{" "}{t("dash_or")}{" "}
              <Link to="/app/video-producer" className="text-blue-600 font-semibold">{t("side_video")}</Link></>)}.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {recent.map((r) => (
                <li key={r.id} className="px-5 py-4 flex items-center justify-between gap-3 text-sm" data-testid={`dash-recent-${r.id}`}>
                  <Link to={`/app/projects/${r.id}`} className="flex items-center gap-3 min-w-0 flex-1 hover:underline">
                    {r.kind === "video" ? (
                      <Clapperboard className="w-4 h-4 text-orange-600 shrink-0" />
                    ) : (
                      <FolderClock className="w-4 h-4 text-orange-600 shrink-0" />
                    )}
                    <span className="font-medium text-zinc-900 truncate">{r.title}</span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.kind === "video" && (
                      <button onClick={(e) => quickDownload(e, r.id)} disabled={downloadingId === r.id}
                        data-testid={`dash-download-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded border border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                        {downloadingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        MP4
                      </button>
                    )}
                    <span className="text-xs text-zinc-500 hidden sm:inline">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
